import { h, Fragment } from 'preact'
import { useEffect } from 'preact/hooks'
import { useStateValue } from '../../state-management/store'
import { SelfSchedulerHeader } from '../self-scheduler-header'
import { CustomRedirectHeader } from '../custom-redirect-header'
import { CenterSpinner } from '../spinner'
import { PatientTypeSelector } from '../patient-type-selector'
import { NewPatientForm } from '../new-patient-form'
import { ExistingPatientForm } from '../existing-patient-form'
import { AdditionalInformation } from '../additional-information'
import { BookingStep, PatientType } from '../../models/enums'
import { LocationsSelector } from '../locations-selector'
import { ValidationScreen } from '../validation-screen'
import { ErrorMessage } from '../error-message'
import { Reserving } from '../reserving'
import { Booked } from '../booked'
import { CustomBookedPage } from '../custom-booked-page'
import { BookingErrored } from '../booking-errored'
import { UnavailableWidget } from '../unavailable-widget'
import { DateTimeSelector } from '../date-time-selector'
import { ExpiredInvitation } from '../expired-invitation'
import { fetchCustomization, fetchLocations, loadChairfillData, setStartingURL } from '../../state-management/actions'
import locationHash from '../../shared/locationHash'
import globalVars from '../../shared/global'
import { useAsync } from '../../shared/custom-hooks/useAsync'

import * as style from './style.css'

const SelfScheduler: preact.FunctionalComponent = () => {
    const [{ expiredRSVPData, locations, bookingStep, patientType, customization }, dispatch] = useStateValue()

    const { run: runFetchCustomization, status: fetchCustomizationStatus, error: fetchCustomizationError } = useAsync({
        status: 'pending',
    })
    const { run: runFetchLocations, status: fetchLocationsStatus, error: fetchLocationsError } = useAsync({
        status: 'pending',
    })
    const { run: runLoadChairfillData, status: loadChairfillDataStatus } = useAsync()

    const customRedirect = customization?.scheduling_additional_features_and_fields?.custom_redirect

    const hasCustomRedirectEnabled = Boolean(
        Boolean(customRedirect?.enabled) && customRedirect?.url.toString() !== '' && customRedirect?.target
    )

    const customRedirectUrl = customRedirect?.url || ''
    const customRedirectTarget = customRedirect?.target || ''
    const customRedirectSameWindow = Boolean(customRedirectTarget === 'same_window')
    const redirectToSameWindow = Boolean(hasCustomRedirectEnabled && customRedirectSameWindow)

    useEffect(() => {
        runFetchCustomization(fetchCustomization(dispatch))
    }, [dispatch, runFetchCustomization])

    useEffect(() => {
        if (fetchCustomizationStatus !== 'pending') {
            runFetchLocations(fetchLocations(dispatch))

            if (globalVars.isRSVP) {
                runLoadChairfillData(loadChairfillData(dispatch, locationHash.patientId, locationHash.chairfillId))
            }

            setStartingURL(dispatch, document.referrer)
        }
    }, [fetchCustomizationStatus, runFetchLocations, runLoadChairfillData])

    const isLoading =
        fetchCustomizationStatus === 'pending' ||
        fetchLocationsStatus === 'pending' ||
        loadChairfillDataStatus === 'pending'

    if (isLoading) {
        return <CenterSpinner />
    }

    if (globalVars.isRSVP && expiredRSVPData?.isAppointmentScheduled) {
        if (locations?.length === 0) {
            return null
        }

        const location = locations.find(({ id }) => id === expiredRSVPData.locationId)

        if (location) {
            return (
                <ExpiredInvitation
                    practiceName={expiredRSVPData.practiceName}
                    website={expiredRSVPData.websiteURL}
                    location={location}
                />
            )
        } else {
            return <ErrorMessage>The location for this appointment doesn&apos;t exist anymore!</ErrorMessage>
        }
    }

    return (
        <Fragment>
            <section class={globalVars.isMounted ? style.selfSchedulerMounted : style.selfScheduler}>
                {bookingStep === BookingStep.BOOKED && redirectToSameWindow ? (
                    <CustomRedirectHeader />
                ) : (
                    <SelfSchedulerHeader />
                )}
                {fetchCustomizationError || fetchLocationsError ? (
                    <UnavailableWidget />
                ) : (
                    <Fragment>
                        {bookingStep === BookingStep.LOCATION && <LocationsSelector />}
                        {bookingStep === BookingStep.PATIENT_TYPE && <PatientTypeSelector />}
                        {bookingStep === BookingStep.DETAILS && patientType === PatientType.NEW && <NewPatientForm />}
                        {bookingStep === BookingStep.DETAILS && patientType === PatientType.EXISTING && (
                            <ExistingPatientForm />
                        )}
                        {bookingStep === BookingStep.ADDITIONAL_INFO && <AdditionalInformation />}
                        {bookingStep === BookingStep.DATETIME && <DateTimeSelector />}
                        {bookingStep === BookingStep.CONFIRM && <ValidationScreen />}
                        {bookingStep === BookingStep.RESERVING && <Reserving />}
                        {bookingStep === BookingStep.TO_BE_BOOKED && <Booked />}
                        {bookingStep === BookingStep.BOOKED && !redirectToSameWindow && (
                            <Booked
                                isBookingCompleted={true}
                                hasCustomRedirectEnabled={hasCustomRedirectEnabled}
                                customRedirectUrl={customRedirectUrl}
                            />
                        )}
                        {bookingStep === BookingStep.BOOKED && redirectToSameWindow && (
                            <CustomBookedPage customRedirectUrl={customRedirectUrl} />
                        )}
                        {bookingStep === BookingStep.BOOKING_FAILED && <BookingErrored />}
                    </Fragment>
                )}
            </section>

            {!locationHash.mounted && <div class={style.overlay}></div>}
        </Fragment>
    )
}

export default SelfScheduler
